// Same product, delivered from the same branch, on more than one basket line.
// Quick add keeps a line per branch, but a supplier can deliver two of those
// branches' lines from one (borrowed) branch; they are then merged so stock
// is checked on the total and the product is ordered once.
import type { MergeCartLineRequest } from '../auth/services/cart-api'
import type { CartItem } from '../types'

/** Basket lines of one product that one branch will deliver. */
export interface SameDeliveryLines {
  lines: MergeCartLineRequest[]
  productName: string
  // The branch that actually delivers them.
  deliveringBranchName: string
}

/**
 * Group delivery lines by supplier + product + delivering branch (the
 * borrowed source when there is one, else the line's own branch — the
 * basket's grouping rule) and return the groups holding two or more lines.
 * A line counts only when delivery is its saved method (the server merges
 * nothing else). Callers pass only lines whose live stock has loaded, so the
 * delivering branch is the supplier's current answer.
 *
 * @param items - Basket lines with live stock attached.
 * @param getMethod - The collection / delivery method the page shows a line under.
 */
export function findSameDeliveryLines(
  items: ReadonlyArray<CartItem>,
  getMethod: (item: CartItem) => 'pickup' | 'delivery',
): SameDeliveryLines[] {
  const linesByKey = new Map<string, CartItem[]>()
  for (const item of items) {
    if (!item.id || item.fulfillmentMethod !== 'delivery' || getMethod(item) !== 'delivery') {
      continue
    }
    const deliveringBranchId = item.deliverySourceBranchId ?? item.branchId
    if (deliveringBranchId == null) continue
    const key = `${item.supplierId}|${item.productId}|${deliveringBranchId}`
    linesByKey.set(key, [...(linesByKey.get(key) ?? []), item])
  }
  return Array.from(linesByKey.values())
    .filter((items) => items.length > 1)
    .map((items) => {
      const borrowedLine = items.find((item) => item.deliverySourceBranchName)
      return {
        lines: items.map((item) => ({
          item_id: String(item.id),
          delivery_source_branch_id:
            item.deliverySourceBranchId != null ? String(item.deliverySourceBranchId) : null,
        })),
        productName: items[0].productName,
        deliveringBranchName:
          borrowedLine?.deliverySourceBranchName ?? items[0].branchName,
      }
    })
}
